import { useState, useEffect } from 'react';
import type { TaxScenario } from '@/tax/types';
import { useSandboxStore } from '@/hooks/useSandboxStore';
import { variantToInput, variantAutoName, makeDefaultVariant, scenarioToVariant, resolveVariantSuffix, VariantCard, type StrategyVariant } from '@/components/wizard/steps/StrategyBuilderStep';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';

interface AddStrategySheetProps {
  /** The baseline scenario for this client (null = sheet is closed, unless editingScenario is set) */
  baseline: TaxScenario | null;
  /** All existing scenarios for this client */
  allClientScenarios: TaxScenario[];
  userId: string;
  onSaved: (scenario: TaxScenario) => void;
  onClose: () => void;
  /** When set, the sheet edits this existing variant instead of adding a new one. */
  editingScenario?: TaxScenario | null;
}

export default function AddStrategySheet({ baseline, allClientScenarios, userId, onSaved, onClose, editingScenario }: AddStrategySheetProps) {
  const store = useSandboxStore();
  const [variant, setVariant] = useState<StrategyVariant>(() =>
    editingScenario ? scenarioToVariant(editingScenario, baseline ?? undefined) : makeDefaultVariant()
  );
  const [saving, setSaving] = useState(false);

  const isEditing = !!editingScenario;
  const isOpen = baseline !== null || isEditing;

  // Re-seed variant state when the sheet opens on a different target.
  useEffect(() => {
    if (!isOpen) return;
    setVariant(editingScenario ? scenarioToVariant(editingScenario, baseline ?? undefined) : makeDefaultVariant());
  }, [editingScenario?.id, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      setVariant(makeDefaultVariant());
    }
  };

  const handleSave = async () => {
    if (!baseline) return;
    const clientName = baseline.input.name.replace(/\s*\(.*\)$/, '').trim();
    const autoName = variantAutoName(variant);
    if (autoName === 'Strategy') {
      toast.error('Enable at least one strategy', { description: 'Toggle on Alt or Roth to save a scenario.' });
      return;
    }
    // Editing an existing variant keeps an advisor's nickname (a stored suffix
    // that differs from the auto-name of the config the sheet opened with).
    const suffix = isEditing
      ? resolveVariantSuffix(editingScenario!.input.name, variantAutoName(scenarioToVariant(editingScenario!, baseline)), autoName)
      : autoName;
    const variantInput = variantToInput(baseline.input, variant, clientName, suffix);
    setSaving(true);
    try {
      const saved = isEditing
        ? await store.updateScenario(editingScenario!.id, variantInput)
        : await store.createScenario(variantInput, userId, baseline.clientId);
      onSaved(saved);
      setVariant(makeDefaultVariant());
    } catch (err: any) {
      toast.error('Error', { description: sanitizeError(err) });
    } finally {
      setSaving(false);
    }
  };

  const baselineResults = baseline?.results ?? null;
  const clientDisplayName = baseline ? baseline.input.name.replace(/\s*\(.*\)$/, '').trim() : '';

  // Compute the card's scenario-slot index (0-based among variants).
  // VariantCard renders "Scenario {index + 2}", so baseline + 2 variants → next add gets index 2 → "Scenario 4".
  // Editing an existing variant uses its current slot.
  const variantsSorted = allClientScenarios
    .filter(s => s.id !== baseline?.id)
    .sort((a, b) =>
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      a.createdAt.localeCompare(b.createdAt)
    );
  const cardIndex = editingScenario
    ? Math.max(0, variantsSorted.findIndex(s => s.id === editingScenario.id))
    : variantsSorted.length;

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="mb-5">
          <SheetTitle>{isEditing ? 'Edit Strategy' : 'Add Strategy'}</SheetTitle>
          <SheetDescription>
            {clientDisplayName ? `Client: ${clientDisplayName}` : ''}
          </SheetDescription>
        </SheetHeader>

        {baseline && (
          <VariantCard
            base={baseline.input}
            variant={variant}
            index={cardIndex}
            baselineResults={baselineResults}
            onUpdate={setVariant}
            onRemove={() => {}}
          />
        )}

        <SheetFooter className="mt-6 gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEditing ? 'Save Changes' : 'Add to Comparison'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
